<template>
    <div class="container mx-auto">
        <div class="flex justify-between">
            <div v-if="showHeader" class="flex" :class="[smallHeader ? 'my-3' : 'mb-1']">
                <div class="flex items-center">
                    <div class="flex">
                        <h1 class="page-header" :class="{ 'text-2xl': smallHeader }">Sites</h1>
                        <div class="ml-3 flex items-center text-gray-600">
                            <div :class="[smallHeader ? 'text-lg' : 'text-2xl']">/</div>
                            <div class="ml-2 text-xl" :class="[smallHeader ? 'pt-2px' : 'pt-1']">{{ sites.filtered.length }} of {{ sites.all.length }}</div>
                        </div>
                    </div>
                    <a v-if="showCreateNew && $gate.can('create', 'site')" class="ml-5 btn btn-small btn-blue" href="/sites/create">
                        <div class="flex items-center justify-center space-x-2">
                            <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                            <p class="text-white">New Site</p>
                        </div>
                    </a>
                </div>
            </div>
            <div v-if="showFilters" class="flex items-center" :class="{'flex-1 justify-end pt-4 pb-3': ! showHeader}">
                <filters
                    data-type="sites"
                    sort-by="building.address_line_1"
                    :filters="filters"
                    :selected-view-option="layout"
                    portal="no-sites-found"
                    @view-change="layout = $event"
                    open-search
                >
                    <template slot="filters">
                        <div>
                            <span class="mr-6">Show Archived Sites</span>
                            <toggle-button v-model="showArchivedSites" color="#38C172" sync></toggle-button>
                        </div>
                    </template>
                </filters>
                <slot name="action-buttons"></slot>
            </div>
        </div>

        <div v-show="layout == 'grid'">
            <paginate name="sites" :list="sites.filtered" :per="perPage" class="paginate-list">
                <div class="mt-8 w-full max-w-lg md:max-w-7xl lg:max-w-[90rem] grid grid-cols-1 lg:grid-cols-2 gap-6" :class="{ 'mx-auto': centerCards, 'xl:grid-cols-3': ! smallHeader }">
                    <div class="card max-h-[220px]" v-for="site in paginated('sites')" :key="site.id" :class="{'hidden': ! showArchivedSites && site.archived_at !== null }">
                        <a
                            :key="site.id"
                            class="h-full min-h-[215px] relative flex items-stretch text-gray-800 cursor-pointer transform hover:no-underline hover:scale-105"
                            :href="site.view_url"
                        >
                            <div class="w-full flex border rounded-lg shadow-lg">
                                <div class="w-full flex flex-col">

                                    <div class="flex flex-1">
                                        <div v-if="site.building.is_in_danger_zone"
                                             class="absolute top-0 right-36 badge border rounded badge-red font-variant-all-small-caps">
                                            Danger Zone
                                        </div>
                                        <div class="absolute top-0 right-0 badge border rounded rounded-br-none" :class="site.status_color">
                                            <div class="flex items-baseline font-variant-all-small-caps space-x-1">
                                                <div v-if="site.lease_or_own == 'Owned'">Owned</div>
                                                <div v-else-if="site.leases.length && site.due_in_days > 0">Due in <span class="font-bold">{{ site.due_in_text }}</span></div>
                                                <div v-else-if="site.leases.length && site.due_in_days == 0">Expired</div>
                                                <div v-else>No Lease Information</div>
                                            </div>
                                        </div>
                                        <div
                                            class="flex w-full rounded-tl-full"
                                            :class="{ 'bg-red-400 text-white': site.is_upcoming || site.building.is_in_danger_zone }"
                                        >
                                            <div
                                                class="w-40 h-full relative"
                                                :class="site.is_upcoming || site.building.is_in_danger_zone ? 'text-red-400' : 'text-white'"
                                            >
                                                <div v-if="site.archived_at !== null" class="mt-4 ml-4 absolute top-0 left-0 z-40" title="Site is Archived">
                                                    <inline-svg name="archived"></inline-svg>
                                                </div>
                                                <img class="w-full h-full max-h-[158px] border-r border-white rounded-tl-lg" :src="site.image.view_url">
                                                <inline-svg name="image-skewer-right"></inline-svg>
                                            </div>
                                            <div class="mx-4 flex flex-1 flex-col justify-center">
                                                <div class="mt-3 flex flex-col justify-between">
                                                    <div class="uppercase">
                                                        <p class="text-lg font-semibold" :title="site.building.address_line_1">{{ site.building.address_line_1 | truncate(22) }}</p>
                                                        <p class="text-sm">{{ site.building.city }}, {{ site.building.state }}</p>
                                                    </div>
                                                    <div class="mt-4 flex gap-x-6" :class="site.is_upcoming || site.building.is_in_danger_zone ? 'text-white' : 'text-gray-800'">
                                                        <div class="flex flex-col">
                                                            <p class="text-sm">{{ site.size | number }}</p>
                                                            <p class="text-xs font-semibold">Sq. Ft.</p>
                                                        </div>
                                                        <div class="flex flex-col">
                                                            <p data-precision="2" :data-originalvalue="site.base_psf" class="is-currency text-sm">{{ formatCurrency(site.base_psf, 2) }}</p>
                                                            <p class="text-xs font-semibold">Base PSF</p>
                                                        </div>
                                                        <div class="flex flex-col">
                                                            <p data-precision="2" :data-originalvalue="site.current_psf" class="is-currency text-sm">{{ formatCurrency(site.current_psf, 2) }}</p>
                                                            <p class="text-xs font-semibold">Current PSF</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="py-4 px-5 flex justify-between bg-gray-100 rounded-b-lg">
                                        <div>
                                            <div v-if="site.utilization" class="badge" :class="site.utilization_color">
                                                <div class="flex items-baseline space-x-1">
                                                    <span class="font-extrabold">{{ site.utilization }}%</span>
                                                    <span class="text-sm font-variant-all-small-caps">Utilized</span>
                                                </div>
                                            </div>

                                            <div v-else></div>
                                        </div>

                                        <div class="badge badge-red">
                                            <div class="flex items-baseline space-x-1">
                                                <span :data-originalvalue="site.shortened_remaining_lease_cost" class="is-shorthand-currency font-extrabold">${{ site.shortened_remaining_lease_cost }}</span>
                                                <span class="text-sm font-variant-all-small-caps">Remaining</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </paginate>
            <paginate-links for="sites" :show-step-links="true" :hide-single-page="true"></paginate-links>
        </div>

        <div v-show="layout == 'table'" :class="{ 'mt-5': (showHeader && ! smallHeader) || showFilters }">
            <table v-if="sites.filtered.length" class="w-full">
                <thead class="cursor-pointer">
                    <tr>
                        <th class="hidden lg:table-cell pl-4"></th>
                        <th class="block pl-6 lg:pl-5">Address</th>
                        <th>Lease or Own</th>
                        <th class="text-center">Due In</th>
                        <th class="hidden lg:table-cell text-right">Base PSF</th>
                        <th class="hidden lg:table-cell text-right">Current PSF</th>
                        <th class="hidden lg:table-cell text-right">Lease Cost</th>
                        <th class="pl-6 hidden lg:table-cell text-right">Remaining</th>
                        <th class="pl-6 hidden lg:table-cell text-right">Utilization</th>
                    </tr>
                </thead>
                <paginate name="sites" :list="sites.filtered" :per="perPage" tag="tbody">
                    <tr
                        v-for="site in paginated('sites')" :key="site.id"
                        :class="{
                            'hidden': ! showArchivedSites && site.archived_at !== null,
                            'row-red': site.is_upcoming || site.building.is_in_danger_zone
                        }"
                    >
                        <td class="pl-0 lg:pl-4 hidden lg:table-cell">
                            <div v-if="site.archived_at !== null" class="absolute mt-1 ml-1 z-40" title="Site is Archived">
                                <inline-svg name="archived" classes="w-2 h-2 text-white fill-current"></inline-svg>
                            </div>
                            <a :href="`#${site.id}`">
                                <img :src="site.building.image.view_url" class="mx-auto w-8 h-8 rounded-md shadow-md">
                            </a>
                            <a href="#_" class="lightbox" :id="`${site.id}`">
                                <img :src="site.building.image.view_url">
                            </a>
                        </td>
                        <td class="leading-normal">
                            <a :href="site.view_url" class="block mx-5">
                                <p class="text-sm font-semibold">{{ site.building.address_line_1 }}</p>
                                <p class="text-xs">{{ site.building.city }}, {{ site.building.state }}</p>
                            </a>
                        </td>
                        <td>{{ site.lease_or_own }}</td>
                        <td class="text-center">
                            <!-- <div class="badge" :class="site.status_color">
                                <div class="flex items-baseline text-sm font-variant-all-small-caps space-x-1">
                                    <div v-if="site.lease_or_own == 'Owned'">Owned</div>
                                    <div v-else-if="site.leases.length && site.due_in_days > 0">Due in <span class="font-bold">{{ site.due_in_text }}</span></div>
                                    <div v-else-if="site.leases.length && site.due_in_days == 0">Expired</div>
                                    <div v-else>No Lease Information</div>
                                </div>
                            </div> -->
                           <div class="badge" :class="site.status_color">
                                <div class="flex items-baseline text-sm font-variant-all-small-caps space-x-1 relative">
                                    <div v-if="site.lease_or_own == 'Owned'">
                                        Owned
                                    </div>
                                    <div v-else-if="site.leases.length && site.due_in_days > 0">
                                        Due in <span class="font-bold">{{ site.due_in_text }}</span>
                                    </div>
                                    <div v-else-if="site.leases.length && site.due_in_days == 0">
                                        Expired
                                    </div>
                                    <div v-else>
                                        No Lease Information
                                    </div>
                                    
                                    <!-- Fixed icon placement -->
                                    <div class="absolute -right-3 -top-1 z-10">
                                        <InfoIcon 
                                            identifier="sites-propertyaddresstabs-dueinxmonths"
                                            class="w-3.5 h-3.5 text-gray-500 hover:text-gray-700"
                                        />
                                    </div>
                                </div>
                            </div>

                        </td>
                        <td data-precision="2" :data-originalvalue="site.base_psf" class="is-currency hidden lg:table-cell text-right">{{ formatCurrency(site.base_psf, 2) }}</td>
                        <td data-precision="2" :data-originalvalue="site.current_psf" class="is-currency hidden lg:table-cell text-right">{{ formatCurrency(site.current_psf, 2) }}</td>
                        <td data-precision="0" :data-originalvalue="site.lease_cost" class="is-currency hidden lg:table-cell text-right">{{ formatCurrency(site.lease_cost) }}</td>
                        <td class="pl-6 hidden lg:table-cell text-right">
                            <!-- <div class="badge badge-red">
                                <div class="flex items-baseline space-x-1">
                                    <span :data-originalvalue="site.shortened_remaining_lease_cost" class="is-shorthand-currency font-extrabold">${{ site.shortened_remaining_lease_cost }}</span>
                                </div>
                            </div> -->
                            <div class="badge badge-red">
                                <div class="flex items-baseline space-x-1 relative">
                                    <span :data-originalvalue="site.shortened_remaining_lease_cost" 
                                        class="is-shorthand-currency font-extrabold">
                                        ${{ site.shortened_remaining_lease_cost }}
                                    </span>
                                    
                                    <!-- Fixed icon container -->
                                    <div class="relative inline-block ml-1">
                                        <div class="absolute -top-2 -right-2 z-50">
                                            <InfoIcon 
                                                identifier="sites-propertyaddresstabs-xremaining"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </td>
                        <td class="pl-6 hidden lg:table-cell text-right font-semibold">
                            <div v-if="site.utilization" class="badge" :class="site.utilization_color">
                                <!-- <div class="flex items-baseline space-x-1">
                                    <span class="font-extrabold">{{ site.utilization }}%</span>
                                    <span class="text-sm font-variant-all-small-caps">Utilized</span>
                                </div> -->
                                <div class="flex items-baseline space-x-1 relative">
                                    <span class="font-extrabold">{{ site.utilization }}%</span>
                                    <span class="text-sm font-variant-all-small-caps">Utilized</span>
                                    
                                    <!-- Fixed icon container -->
                                    <div class="absolute -top-1 right-0 z-10">
                                        <InfoIcon 
                                            identifier="sites-propertyaddresstabs-utilized"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div v-else>-</div>
                        </td>
                    </tr>
                </paginate>
            </table>
            <paginate-links for="sites" :show-step-links="true" :hide-single-page="true"></paginate-links>
        </div>

        <portal-target name="no-sites-found"></portal-target>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
        props: {
            siteData: {
              type: Array,
              required: true
            },

            showHeader: {
              type: Boolean,
              default: true
            },

            smallHeader: {
              type: Boolean,
              default: false
            },

            showFilters: {
              type: Boolean,
              default: true
            },

            showCreateNew: {
              type: Boolean,
              default: true
            },

            centerCards: {
                type: Boolean,
                default: false
            },

            view: {
                type: String,
                default: 'grid'
            },

            perPage: {
                type: Number,
                default: 10
            }
        },

        data() {
            return {
                paginate: ['sites'],
                showArchivedSites: false,
                layout: this.view
            }
        },

        mounted() {
            this.$eventHub.$on('reset-search', () => {
                this.paginate.sites.list = this.sites.filtered;
            });

            this.sites.all = this.siteData
            this.sites.filtered = this.siteData.filter(site => {
                return site.archived_at == null;
            });

            this.$emit('filterRequest');
        },

        computed: {
            ...mapState(['sites']),

            filters() {
                return {
                    showArchivedSites: this.showArchivedSites
                }
            }
        }
    }
</script>
